/**
 * ICalSyncPanel Component
 *
 * Host UI panel for importing and exporting iCal calendars.
 * Import: List feeds, add/remove, sync now.
 * Export: Copyable DARAK iCal URL, generate URL button.
 */

import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { useICalFeeds } from '../hooks/useICalFeeds';

interface ICalSyncPanelProps {
  listingId: string;
}

export const ICalSyncPanel: React.FC<ICalSyncPanelProps> = ({ listingId }) => {
  const {
    feeds,
    exportUrl,
    isLoading,
    isSyncing,
    addFeed,
    removeFeed,
    syncNow,
    generateExportUrl,
  } = useICalFeeds(listingId);

  const [newFeedName, setNewFeedName] = useState('');
  const [newFeedUrl, setNewFeedUrl] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleAddFeed = async () => {
    if (!newFeedName.trim() || !newFeedUrl.trim()) return;

    setIsAdding(true);
    try {
      await addFeed(newFeedName.trim(), newFeedUrl.trim());
      setNewFeedName('');
      setNewFeedUrl('');
      toast.success('Calendar added successfully');
    } catch (err) {
      console.error('Error adding feed:', err);
      toast.error(
        err instanceof Error && err.message
          ? err.message
          : 'Failed to add calendar. Please check the URL.'
      );
    } finally {
      setIsAdding(false);
    }
  };

  const handleSync = async () => {
    try {
      const result = await syncNow();
      if (result.errors?.length) {
        toast.error(`Synced ${result.synced} events with ${result.errors.length} error(s)`);
      } else {
        toast.success(`Synced ${result.synced} events successfully`);
      }
    } catch (err) {
      toast.error('Sync failed. Please try again.');
    }
  };

  const handleCopyUrl = async () => {
    if (!exportUrl) return;
    try {
      await navigator.clipboard.writeText(exportUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback
      const el = document.createElement('textarea');
      el.value = exportUrl;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-6 bg-gray-200 rounded w-40" />
        <div className="h-20 bg-gray-200 rounded-xl" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-gray-900">Calendar Sync</h3>

      {/* Import Section */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-medium text-gray-900">Import calendars</h4>
          {feeds.length > 0 && (
            <button
              onClick={handleSync}
              disabled={isSyncing}
              className="text-sm font-medium text-primary-600 hover:text-primary-700 disabled:opacity-50"
            >
              {isSyncing ? 'Syncing...' : 'Sync now'}
            </button>
          )}
        </div>

        {/* Feed List */}
        {feeds.length > 0 ? (
          <div className="space-y-2 mb-4">
            {feeds.map((feed, idx) => (
              <div
                key={idx}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
              >
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-900 truncate flex items-center gap-1.5">
                    <span
                      className={`w-2 h-2 rounded-full shrink-0 ${
                        feed.lastError
                          ? 'bg-red-500'
                          : feed.lastSynced
                            ? 'bg-green-500'
                            : 'bg-amber-400'
                      }`}
                      title={
                        feed.lastError
                          ? 'Last sync failed'
                          : feed.lastSynced
                            ? 'Syncing OK'
                            : 'Not synced yet'
                      }
                    />
                    {feed.name}
                  </p>
                  <p className="text-xs text-gray-500 truncate">{feed.url}</p>
                  {feed.lastSynced && (
                    <p className="text-xs text-gray-400 mt-0.5">
                      Last synced:{' '}
                      {feed.lastSynced.toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit',
                      })}
                    </p>
                  )}
                  {feed.lastError && (
                    <p className="text-xs text-red-600 mt-0.5 truncate">
                      Last sync failed: {feed.lastError}
                    </p>
                  )}
                </div>
                <button
                  onClick={() => removeFeed(idx)}
                  className="ml-3 text-gray-400 hover:text-red-500 transition-colors"
                  title="Remove feed"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500 mb-4">
            No calendars imported. Add your Airbnb or other iCal feed below.
          </p>
        )}

        {/* Add Feed Form */}
        <div className="space-y-2">
          <input
            type="text"
            value={newFeedName}
            onChange={(e) => setNewFeedName(e.target.value)}
            placeholder="Calendar name (e.g. Airbnb)"
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
          <input
            type="url"
            value={newFeedUrl}
            onChange={(e) => setNewFeedUrl(e.target.value)}
            placeholder="iCal URL (https://...)"
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
          <button
            onClick={handleAddFeed}
            disabled={!newFeedName.trim() || !newFeedUrl.trim() || isAdding}
            className="w-full py-2 text-sm font-medium bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 transition-colors"
          >
            {isAdding ? 'Adding...' : 'Add calendar'}
          </button>
        </div>
      </div>

      {/* Export Section */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <h4 className="font-medium text-gray-900 mb-3">Export calendar</h4>
        <p className="text-sm text-gray-500 mb-3">
          Share this URL with Airbnb or other platforms to sync your DARAK bookings.
        </p>

        {exportUrl ? (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={exportUrl}
                readOnly
                className="flex-1 px-3 py-2 text-sm bg-gray-50 border border-gray-300 rounded-lg text-gray-600"
              />
              <button
                onClick={handleCopyUrl}
                className="px-4 py-2 text-sm font-medium bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>
          </div>
        ) : (
          <button
            onClick={generateExportUrl}
            className="w-full py-2 text-sm font-medium bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
          >
            Generate .ics URL
          </button>
        )}
      </div>
    </div>
  );
};

export default ICalSyncPanel;
